/** TrustiChain API default: rate = target currency units per 1 USD/RLUSD. */
export const DEFAULT_EXCHANGE_QUOTE_DIRECTION = 'unitsPerUsd';

export function normalizeExchangeQuoteDirection(value) {
  const normalized = String(value || DEFAULT_EXCHANGE_QUOTE_DIRECTION).trim();
  if (/^usd\s*per\s*unit$/i.test(normalized) || normalized === 'usdPerUnit') {
    return 'usdPerUnit';
  }
  return DEFAULT_EXCHANGE_QUOTE_DIRECTION;
}

/** Live XRP/USD from api/exchange/rates (`data.xrpUsdRate`, not the fiat `rates[]` list). */
export function readXrpUsdRateFromExchangePayload(payload) {
  if (payload == null) return null;
  if (typeof payload === 'number') {
    return Number.isFinite(payload) && payload > 0 ? payload : null;
  }
  const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
  if (!data || typeof data !== 'object') return null;
  const n = Number(data.xrpUsdRate ?? data.xrpUsd ?? data.xrp_usd_rate ?? data.usdPerXrp);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * USD value of 1 XRP from api/exchange/rates.
 * Fiat quotes live in `data.rates`; XRP/USD is `data.xrpUsdRate` (USD per 1 XRP).
 */
export function getUsdPerXrpFromExchangeRates(
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
  xrpUsdRateOrPayload,
) {
  const fromPayload = readXrpUsdRateFromExchangePayload(xrpUsdRateOrPayload);
  if (fromPayload != null) return fromPayload;

  if (exchangeRates && !Array.isArray(exchangeRates) && typeof exchangeRates === 'object') {
    const nested = readXrpUsdRateFromExchangePayload(exchangeRates);
    if (nested != null) return nested;
    const nestedRates = Array.isArray(exchangeRates.rates)
      ? exchangeRates.rates
      : Array.isArray(exchangeRates.data?.rates)
        ? exchangeRates.data.rates
        : null;
    if (nestedRates) {
      return getUsdPerXrpFromExchangeRates(nestedRates, quoteDirection);
    }
    return null;
  }

  if (!Array.isArray(exchangeRates) || exchangeRates.length === 0) return null;

  const pair = exchangeRates.find(
    (r) =>
      (r.from === 'XRP' && r.to === 'USD') ||
      (r.fromCurrency === 'XRP' && r.toCurrency === 'USD'),
  );
  if (pair != null) {
    const v = Number(pair.rate ?? pair.exchangeRate ?? pair.value);
    if (Number.isFinite(v) && v > 0) return v;
  }

  const reverse = exchangeRates.find(
    (r) =>
      (r.from === 'USD' && r.to === 'XRP') ||
      (r.fromCurrency === 'USD' && r.toCurrency === 'XRP'),
  );
  if (reverse != null) {
    const v = Number(reverse.rate ?? reverse.exchangeRate ?? reverse.value);
    if (Number.isFinite(v) && v > 0) return 1 / v;
  }

  const row = exchangeRates.find((r) => (r.currency || r.code || '').toUpperCase() === 'XRP');
  const n = Number(row?.rate ?? row?.value ?? row?.exchangeRate ?? row?.priceUsd ?? row?.usd);
  if (!Number.isFinite(n) || n <= 0) return null;
  // XRP in rates[] is USD per 1 XRP, matching data.xrpUsdRate. Fiat quoteDirection does not apply.
  return n;
}

const STABLE_SAVINGS_TOKENS = new Set(['USDT', 'USDC', 'RLUSD', 'USD']);

/** USD value of 1 savings token. Stables are pegged; XRP uses the live XRP/USD quote. */
export function getUsdPerSavingsToken(
  ticker,
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
  xrpUsdRate,
) {
  const code = String(ticker || 'XRP').toUpperCase();
  if (STABLE_SAVINGS_TOKENS.has(code)) return 1;
  if (code === 'XRP') {
    return getUsdPerXrpFromExchangeRates(exchangeRates, quoteDirection, xrpUsdRate);
  }
  return null;
}

export function parseSavingsTokenAmount(amount) {
  const n = parseFloat(String(amount ?? '').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

/** USD value of a savings amount at the current exchange rate. */
export function savingsTokenAmountToUsd(
  amount,
  ticker,
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
  xrpUsdRate,
) {
  const n = parseSavingsTokenAmount(amount);
  if (n == null || n <= 0) return null;
  const per = getUsdPerSavingsToken(ticker, exchangeRates, quoteDirection, xrpUsdRate);
  if (per == null || !(per > 0)) return null;
  return n * per;
}

/** Convert a USD/RLUSD total into another fiat using api/exchange/rates rows. */
export function convertUsdTotalToFiatDisplayAmount(
  code,
  usdTotal,
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
) {
  if (!Array.isArray(exchangeRates)) return null;
  const row = exchangeRates.find((r) => (r.currency || r.code || '').toUpperCase() === code);
  const quote = Number(row?.rate ?? row?.value ?? 0);
  if (!Number.isFinite(quote) || quote <= 0) return null;

  const base = Number(usdTotal) || 0;
  if (normalizeExchangeQuoteDirection(quoteDirection) === 'usdPerUnit') {
    return base / quote;
  }
  // unitsPerUsd — e.g. GBP 0.739 means $1 = £0.739
  return base * quote;
}

/** Convert an amount entered in fiat into USD using api/exchange/rates rows. */
export function convertFiatAmountToUsd(
  code,
  fiatAmount,
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
) {
  const amount = Number(fiatAmount);
  if (!Number.isFinite(amount)) return null;
  if (code === 'USD') return amount;
  if (!Array.isArray(exchangeRates)) return null;

  const row = exchangeRates.find((r) => (r.currency || r.code || '').toUpperCase() === code);
  const quote = Number(row?.rate ?? row?.value ?? 0);
  if (!Number.isFinite(quote) || quote <= 0) return null;

  if (normalizeExchangeQuoteDirection(quoteDirection) === 'usdPerUnit') {
    return amount * quote;
  }
  return amount / quote;
}

/** RLUSD is pegged 1:1 to USD on TrustiChain. */
export function convertFiatAmountToRlusd(
  code,
  fiatAmount,
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
) {
  return convertFiatAmountToUsd(code, fiatAmount, exchangeRates, quoteDirection);
}

export function formatRlusdAmount(amount, options = {}) {
  const minDigits = options.minimumFractionDigits ?? 2;
  const maxDigits = options.maximumFractionDigits ?? 6;
  return `${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
  })} RLUSD`;
}

export function formatConvertedFiatAmount(code, amount, fractionDigitsOverride) {
  const fractionDigits =
    fractionDigitsOverride ??
    (code === 'JPY' || code === 'KRW' ? 0 : 2);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch (_) {
    return `${code} ${amount.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })}`;
  }
}

export function formatUsdAmount(usdTotal) {
  return `$${Number(usdTotal || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function resolveSummaryXrpBalance(dashboardData, getBalanceValue) {
  if (dashboardData?.balance?.xrp !== undefined && dashboardData?.balance?.xrp !== null) {
    return Number(dashboardData.balance.xrp);
  }
  if (typeof getBalanceValue === 'function') {
    const v = getBalanceValue(dashboardData, 'xrp');
    if (v !== null && v !== undefined) return Number(v);
  }
  return 0;
}

export function resolveSummaryUsdBalance(dashboardData, getBalanceValue) {
  if (typeof getBalanceValue === 'function') {
    const v = getBalanceValue(dashboardData, 'usd');
    if (v !== null && v !== undefined) return Number(v);
  }
  if (dashboardData?.balance?.usd !== undefined && dashboardData?.balance?.usd !== null) {
    return Number(dashboardData.balance.usd);
  }
  return 0;
}

export function computePortfolioUsdTotalFromSummary({
  dashboardData,
  exchangeRates,
  getExchangeRate,
  getBalanceValue,
}) {
  const xrpBalance = resolveSummaryXrpBalance(dashboardData, getBalanceValue);

  if (
    Number(xrpBalance) > 0 &&
    Array.isArray(exchangeRates) &&
    exchangeRates.length > 0 &&
    typeof getExchangeRate === 'function'
  ) {
    const xrpToUsdRate = getExchangeRate('XRP', 'USD');
    if (xrpToUsdRate) return Number(xrpBalance) * Number(xrpToUsdRate);

    const usdRate = exchangeRates.find(
      (r) =>
        (r.from === 'XRP' && r.to === 'USD') ||
        r.currency === 'USD' ||
        r.code === 'USD',
    );
    if (usdRate?.rate) return Number(xrpBalance) * Number(usdRate.rate);
  }

  return resolveSummaryUsdBalance(dashboardData, getBalanceValue);
}

export function formatPortfolioPrimaryDisplay(
  currency,
  usdTotal,
  xrpAmount,
  exchangeRates,
  quoteDirection = DEFAULT_EXCHANGE_QUOTE_DIRECTION,
) {
  const code = currency || 'USD';
  if (code === 'USD') return formatUsdAmount(usdTotal);
  if (code === 'XRP') {
    return `${Number(xrpAmount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    })} XRP`;
  }
  const amt = convertUsdTotalToFiatDisplayAmount(
    code,
    usdTotal,
    exchangeRates,
    quoteDirection,
  );
  if (amt == null) return '—';
  return formatConvertedFiatAmount(code, amt);
}

/**
 * Format a wallet's USD value in the user's display currency.
 * When XRP is selected, native XRP balances stay in XRP and stablecoins convert via XRP/USD.
 */
export function formatWalletUsdInDisplayCurrency({
  usdValue,
  nativeCode,
  nativeAmount,
  displayCurrency,
  formatFromUsd,
  getExchangeRate,
}) {
  const code = String(nativeCode || '').toUpperCase();
  const amount = Number(nativeAmount ?? 0);
  const usd = Number(usdValue ?? 0);

  if ((displayCurrency || 'USD') === 'XRP') {
    if (code === 'XRP') {
      return formatFromUsd(usd, { xrpAmount: amount });
    }
    const xrpToUsd = typeof getExchangeRate === 'function' ? getExchangeRate('XRP', 'USD') : null;
    if (xrpToUsd != null && Number(xrpToUsd) > 0) {
      return formatFromUsd(usd, { xrpAmount: usd / Number(xrpToUsd) });
    }
  }

  return formatFromUsd(usd);
}

/** Format a USD-denominated API amount in the user's display currency. */
export function formatDisplayAmountFromUsd(
  usdAmount,
  displayCurrency,
  exchangeRates,
  options = {},
) {
  const usdTotal = Number(usdAmount) || 0;
  const code = displayCurrency || 'USD';
  const minDigits = options.minimumFractionDigits;
  const maxDigits = options.maximumFractionDigits;

  if (code === 'USD') {
    return `$${usdTotal.toLocaleString('en-US', {
      minimumFractionDigits: minDigits ?? 2,
      maximumFractionDigits: maxDigits ?? 2,
    })}`;
  }

  if (code === 'XRP') {
    if (options.xrpAmount != null) {
      return `${Number(options.xrpAmount).toLocaleString('en-US', {
        minimumFractionDigits: minDigits ?? 2,
        maximumFractionDigits: maxDigits ?? 6,
      })} XRP`;
    }
    if (options.isLoadingRates) return '…';
    return formatUsdAmount(usdTotal);
  }

  if (options.isLoadingRates) return '…';

  const amt = convertUsdTotalToFiatDisplayAmount(
    code,
    usdTotal,
    exchangeRates,
    options.quoteDirection,
  );
  if (amt == null) return '—';

  const fractionDigits =
    maxDigits ?? (code === 'JPY' || code === 'KRW' ? 0 : 2);
  return formatConvertedFiatAmount(code, amt, fractionDigits);
}

export function formatPortfolioSecondaryDisplay(currency, usdTotal, xrpAmount) {
  const code = currency || 'USD';
  if (code === 'USD') {
    return `${Number(xrpAmount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    })} XRP`;
  }
  return formatUsdAmount(usdTotal);
}
